"use client"

import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, MoreHorizontal } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { CardDescription, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { RequestPriority, RequestStatus, WorkingModel } from "./resource-requests/create/types"

// Define the type for our resource request data
interface ResourceRequest {
  id: string
  title: string
  department: string
  position: string
  numberOfPositions: number
  workingModels: WorkingModel[]
  status: RequestStatus
  priority: RequestPriority
  requestedBy: {
    id: string
    name: string
  }
  requestDate: Date
  targetStartDate?: Date
  targetEndDate?: Date
  requestDeadline?: Date
  description?: string
  projectCode?: string
}

// Mock data for demonstration
const data: ResourceRequest[] = [
  {
    id: "1",
    title: "Senior React Developer",
    department: "Engineering",
    position: "Developer",
    numberOfPositions: 2,
    workingModels: [WorkingModel.HYBRID, WorkingModel.REMOTE],
    status: RequestStatus.OPEN,
    priority: RequestPriority.HIGH,
    requestedBy: {
      id: "u1",
      name: "John Doe"
    },
    requestDate: new Date("2023-01-15"),
    targetStartDate: new Date("2023-02-15"),
    projectCode: "PRJ-2023-001"
  },
  {
    id: "2",
    title: "Sales Manager",
    department: "Sales",
    position: "Manager",
    numberOfPositions: 1,
    workingModels: [WorkingModel.ONSITE],
    status: RequestStatus.PENDING,
    priority: RequestPriority.MEDIUM,
    requestedBy: {
      id: "u2",
      name: "Jane Smith"
    },
    requestDate: new Date("2023-02-20"),
    targetStartDate: new Date("2023-03-20"),
    projectCode: "PRJ-2023-002"
  },
  {
    id: "3",
    title: "DevOps Engineer",
    department: "Infrastructure",
    position: "Engineer",
    numberOfPositions: 3,
    workingModels: [WorkingModel.REMOTE],
    status: RequestStatus.OPEN,
    priority: RequestPriority.URGENT,
    requestedBy: {
      id: "u1",
      name: "John Doe"
    },
    requestDate: new Date("2023-03-05"),
    targetStartDate: new Date("2023-04-01"),
    targetEndDate: new Date("2023-12-31"),
    projectCode: "PRJ-2023-003"
  },
  {
    id: "4",
    title: "UI/UX Designer",
    department: "Design",
    position: "Designer",
    numberOfPositions: 1,
    workingModels: [WorkingModel.HYBRID],
    status: RequestStatus.CLOSED,
    priority: RequestPriority.MEDIUM,
    requestedBy: {
      id: "u3",
      name: "David Johnson"
    },
    requestDate: new Date("2023-03-10"),
    targetStartDate: new Date("2023-04-10"),
    projectCode: "PRJ-2023-004"
  },
  {
    id: "5",
    title: "Freelance Content Writer",
    department: "Marketing",
    position: "Content Writer",
    numberOfPositions: 2,
    workingModels: [WorkingModel.FREELANCER],
    status: RequestStatus.OPEN,
    priority: RequestPriority.HIGH,
    requestedBy: {
      id: "u2",
      name: "Jane Smith"
    },
    requestDate: new Date("2023-03-15"),
    targetStartDate: new Date("2023-04-15"),
    requestDeadline: new Date("2023-04-01"),
    projectCode: "PRJ-2023-005"
  },
]

// Status badge component
function StatusBadge({ status }: { status: RequestStatus }) {
  const statusStyles = {
    [RequestStatus.DRAFT]: "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100",
    [RequestStatus.OPEN]: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
    [RequestStatus.PENDING]: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100",
    [RequestStatus.CLOSED]: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
  }

  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status]}`}>{status}</span>
}

// Priority badge component
function PriorityBadge({ priority }: { priority: RequestPriority }) {
  const priorityStyles = {
    [RequestPriority.URGENT]: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100",
    [RequestPriority.HIGH]: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100",
    [RequestPriority.MEDIUM]: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100",
    [RequestPriority.LOW]: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100",
  }

  return <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityStyles[priority]}`}>{priority}</span>
}

// Working model badge component
function WorkingModelBadge({ models }: { models: WorkingModel[] }) {
  if (!models.length) return null

  return (
    <div className="flex flex-wrap gap-1">
      {models.map((model) => (
        <span key={model} className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100">
          {model}
        </span>
      ))}
    </div>
  )
}

export default function ResourceRequestTable() {
  const navigate = useNavigate()
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [statusFilter, setStatusFilter] = useState<string>("all")
  const [priorityFilter, setPriorityFilter] = useState<string>("all")
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  // Handle row click to navigate to detail page
  const handleRowClick = (id: string) => {
    navigate(`/resource-requests/${id}`)
  }

  // Define columns for the table
  const columns: ColumnDef<ResourceRequest>[] = [
    {
      accessorKey: "title",
      header: ({ column }) => {
        return (
          <Button variant="ghost" onClick={(e) => {
            e.stopPropagation() // Prevent row click when sorting
            column.toggleSorting(column.getIsSorted() === "asc")
          }}>
            Title
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div className="font-medium text-primary">
          {row.getValue("title")}
        </div>
      ),
    },
    {
      accessorKey: "position",
      header: "Position",
      cell: ({ row }) => <div>{row.getValue("position")}</div>,
    },
    {
      accessorKey: "department",
      header: "Department",
      cell: ({ row }) => <div>{row.getValue("department")}</div>,
    },
    {
      accessorKey: "workingModels",
      header: "Working Model",
      cell: ({ row }) => <WorkingModelBadge models={row.getValue("workingModels")} />,
    },
    {
      accessorKey: "numberOfPositions",
      header: ({ column }) => {
        return (
          <Button variant="ghost" onClick={(e) => {
            e.stopPropagation() // Prevent row click when sorting
            column.toggleSorting(column.getIsSorted() === "asc")
          }}>
            Quantity
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => <div className="text-center">{row.getValue("numberOfPositions")}</div>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <StatusBadge status={row.getValue("status")} />,
    },
    {
      accessorKey: "priority",
      header: "Priority",
      cell: ({ row }) => <PriorityBadge priority={row.getValue("priority")} />,
    },
    {
      accessorKey: "requestDate",
      header: ({ column }) => {
        return (
          <Button variant="ghost" onClick={(e) => {
            e.stopPropagation() // Prevent row click when sorting
            column.toggleSorting(column.getIsSorted() === "asc")
          }}>
            Request Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        )
      },
      cell: ({ row }) => {
        const date = row.getValue<Date>("requestDate")
        return <div>{date.toLocaleDateString()}</div>
      },
    },
    {
      accessorKey: "requestedBy",
      header: "Requested By",
      cell: ({ row }) => {
        const requestedBy = row.getValue<ResourceRequest["requestedBy"]>("requestedBy")
        return <div>{requestedBy.name}</div>
      },
    },
    {
      accessorKey: "projectCode",
      header: "Project Code",
      cell: ({ row }) => <div>{row.getValue<string>("projectCode") || "-"}</div>,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const request = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="h-8 w-8 p-0"
                onClick={(e) => e.stopPropagation()} // Prevent row click when clicking dropdown
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}> {/* Prevent row click when using dropdown */}
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => {
                navigator.clipboard.writeText(request.id)
                // Show toast or feedback
              }}>
                Copy ID
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={`/resource-requests/${request.id}`}>View Details</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/resource-requests/${request.id}/edit`}>Edit Request</Link>
              </DropdownMenuItem>
              <DropdownMenuItem>Change Status</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  // Create the table instance with original data, not filtered data
  const table = useReactTable({
    data: data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  })

  // Filter data based on selected filters
  const filteredData = data.filter((item) => {
    if (statusFilter !== "all" && item.status !== statusFilter) {
      return false
    }
    if (priorityFilter !== "all" && item.priority !== priorityFilter) {
      return false
    }
    return true
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <CardTitle className="text-xl">Resource Requests</CardTitle>
          <CardDescription>Manage your resource requests</CardDescription>
        </div>
        <Link to="/resource-requests/create">
          <Button className="ml-auto">Create Request</Button>
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Search input */}
        <div className="flex w-full sm:w-auto gap-2 items-center">
          <Input
            placeholder="Search by title..."
            value={(table.getColumn("title")?.getFilterValue() as string) || ""}
            onChange={(e) => table.getColumn("title")?.setFilterValue(e.target.value)}
            className="w-full sm:w-[300px]"
            onClick={(e) => e.stopPropagation()} // Prevent row click when using input
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center justify-end w-full sm:w-auto">
          <Select 
            value={statusFilter} 
            onValueChange={setStatusFilter}
            onOpenChange={(open) => {
              if (open) {
                // Add event listener to prevent propagation when open
                document.addEventListener('click', (e) => e.stopPropagation(), { once: true })
              }
            }}
          >
            <SelectTrigger className="w-[180px]" onClick={(e) => e.stopPropagation()}>
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent onClick={(e) => e.stopPropagation()}>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value={RequestStatus.DRAFT}>Draft</SelectItem>
              <SelectItem value={RequestStatus.OPEN}>Open</SelectItem>
              <SelectItem value={RequestStatus.PENDING}>Pending</SelectItem>
              <SelectItem value={RequestStatus.CLOSED}>Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select 
            value={priorityFilter} 
            onValueChange={setPriorityFilter}
            onOpenChange={(open) => {
              if (open) {
                // Add event listener to prevent propagation when open
                document.addEventListener('click', (e) => e.stopPropagation(), { once: true })
              }
            }}
          >
            <SelectTrigger className="w-[180px]" onClick={(e) => e.stopPropagation()}>
              <SelectValue placeholder="Filter by priority" />
            </SelectTrigger>
            <SelectContent onClick={(e) => e.stopPropagation()}>
              <SelectItem value="all">All Priorities</SelectItem>
              <SelectItem value={RequestPriority.LOW}>Low</SelectItem>
              <SelectItem value={RequestPriority.MEDIUM}>Medium</SelectItem>
              <SelectItem value={RequestPriority.HIGH}>High</SelectItem>
              <SelectItem value={RequestPriority.URGENT}>Urgent</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} onClick={(e) => e.stopPropagation()}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow 
                  key={row.id} 
                  data-state={row.getIsSelected() && "selected"}
                  onClick={() => handleRowClick(row.original.id)}
                  className="cursor-pointer transition-colors hover:bg-muted/50 focus:bg-muted/70"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    // Allow keyboard navigation (Enter or Space)
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleRowClick(row.original.id)
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-end space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation()
            table.previousPage()
          }}
          disabled={!table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={(e) => {
            e.stopPropagation()
            table.nextPage()
          }} 
          disabled={!table.getCanNextPage()}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

